import { defineStore } from "pinia";
import {
  GroupsApi,
  UserDetailApi,
  type CustomFieldLinkResponseModel,
  type UserDetailCounts,
  type UserDetailHabilitationsPagination,
  type UserDetailLearningPathsPagination,
  type UserDetailRequestsPagination,
  type UserDetailTrainingsPagination,
} from "@simbelapp/order-sdk";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useFormats } from "~/composables/utils/useFormat";
import { useFilters } from "~/composables/filters/useFilters";
import { usePagination } from "~/composables/paginations/usePagination";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { ContextFiltersEnum } from "~/utils/enums/context-filters.enums";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import type { RolesEnums } from "~/utils/enums/roles.enums";
import type { CountriesEnum, GenderEnum, UserLanguageEnum, UserStatusEnum } from "~/utils/enums/users.enums";
import type { IUserDetails, IUserGroups } from "~/utils/interfaces/users-interfaces";

/**
 * Single source of truth for the "fiche utilisateur" modal: its open state, the active user,
 * the general info (userDetails + groups), and the activity lists (demandes / formations /
 * habilitations) — plus every call related to a user's details.
 *
 * All tabs use the dedicated per-user endpoints (UserDetailApi). /general returns the full
 * CollaboratorDetails (incl. roles / custom fields / legal entity) consumed by the Général edit form.
 */
interface IUserDetailStore {
  showModal: boolean;
  userId: string | null;
  userDetails: IUserDetails | null;
  userGroups: IUserGroups | null;
  userRequests: UserDetailRequestsPagination | null;
  userTrainings: UserDetailTrainingsPagination | null;
  userLearningPaths: UserDetailLearningPathsPagination | null;
  userHabilitations: UserDetailHabilitationsPagination | null;
  userCounts: UserDetailCounts | null;
}

/**
 * Monotonic token for the Parcours tab's in-flight request. The `userId` check inside
 * fetchLearningPaths only catches a switch to another user; two requests for the SAME user — a sort
 * click landing on top of a page click — can still resolve out of order and leave the older page on
 * screen. Committing only the latest token fixes that.
 */
let learningPathsRequestSeq = 0;

export const useUserDetailStore = defineStore("userDetail", {
  state: (): IUserDetailStore => ({
    showModal: false,
    userId: null,
    userDetails: null,
    userGroups: null,
    userRequests: null,
    userTrainings: null,
    userLearningPaths: null,
    userHabilitations: null,
    userCounts: null,
  }),
  getters: {
    /** The zone orders keeps on the user, learned from their browser at sign-in. */
    timezone(): string | null {
      return this.userDetails?.timezone ?? null;
    },
  },
  actions: {
    /** Open the modal on a given user and load the general info. */
    open(userId: string) {
      this.userId = userId;
      this.userDetails = null;
      this.userGroups = null;
      this.userRequests = null;
      this.userTrainings = null;
      this.userLearningPaths = null;
      this.userHabilitations = null;
      this.userCounts = null;
      this.showModal = true;
      this.fetchUserDetails();
      this.fetchCounts();
    },

    /** Close the modal and clear the per-user data (keeps the underlying lists untouched). */
    close() {
      this.showModal = false;
      this.userId = null;
      this.userDetails = null;
      this.userGroups = null;
      this.userRequests = null;
      this.userTrainings = null;
      this.userLearningPaths = null;
      this.userHabilitations = null;
      this.userCounts = null;
    },

    async fetchUserDetails() {
      if (!this.userId) return;
      // Guard against user switches: a late response must not overwrite a newer user's state.
      const requestUserId = this.userId;
      const feedback = useFeedback();
      const { toDateDisplayNoUTC } = useFormats();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        const data = await apiInstance.getUserDetailGeneral({ userId: requestUserId });
        if (this.userId !== requestUserId) return;
        if (data) {
          // Spread the SDK model and only override the fields that need a transform/cast or rename.
          this.userDetails = {
            ...data,
            status: data.status as UserStatusEnum,
            birthDate: data.birthDate ? toDateDisplayNoUTC(data.birthDate) : null,
            language: data.language as UserLanguageEnum,
            contractCountry: data.contractCountry as CountriesEnum,
            contractStartDate: data.contractStartDate ? toDateDisplayNoUTC(data.contractStartDate) : null,
            contractEndDate: data.contractEndDate ? toDateDisplayNoUTC(data.contractEndDate) : null,
            gender: data.gender as GenderEnum,
            disableNotification: false,
            companyId: data.mainEntityId,
            roles: data.roles
              ? data.roles.map((role) => ({ roleId: role.roleId, roleName: role.roleName as RolesEnums }))
              : [],
            customFields: (data.customFields ?? []) as CustomFieldLinkResponseModel[],
          };
          await this.fetchUserGroups();
        }
      } catch (error) {
        feedback.error("Une erreur est survenue", "small");
      }
    },

    async fetchUserGroups() {
      if (!this.userId) return;
      const requestUserId = this.userId;
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, GroupsApi);
        const data = await apiInstance.getUserGroups({ userId: requestUserId });
        if (this.userId !== requestUserId) return;
        this.userGroups = data ?? null;
      } catch (error) {
        feedback.error("Une erreur est survenue", "small");
        if (this.userId === requestUserId) this.userGroups = null;
      }
    },

    /**
     * Demandes tab — GET /userDetail/user-requests/:userId.
     * Standard grid (FindOptionsDto): offset/limit + sorting_column/order + the generic `filters`
     * array (request_period selected via the FiltersContainer in USER_DETAIL_REQUESTS_CONTEXT).
     */
    async fetchRequests() {
      if (!this.userId) return;
      const requestUserId = this.userId;
      const pagination = usePagination(PaginationKeysEnum.USER_DETAIL_REQUESTS_PAGINATION);
      const filters = useFilters(ContextFiltersEnum.USER_DETAIL_REQUESTS_CONTEXT);

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        const response = await apiInstance.getUserDetailRequests({
          userId: requestUserId,
          ...queryString,
          ...filtersQuery,
          sortingColumn: paginateOptions.sortingColumn,
        });
        if (this.userId !== requestUserId) return;
        if (response) {
          this.userRequests = response;
        }
      } catch (error) {
        feedback.error(`erreur interne`, "small");
      }
    },

    /**
     * Formations tab — GET /userDetail/user-trainings/:userId (incl. brouillons).
     * Same standard grid + `filters` array (period selected via USER_DETAIL_TRAININGS_CONTEXT).
     */
    async fetchTrainings() {
      if (!this.userId) return;
      const requestUserId = this.userId;
      const pagination = usePagination(PaginationKeysEnum.USER_DETAIL_TRAININGS_PAGINATION);
      const filters = useFilters(ContextFiltersEnum.USER_DETAIL_TRAININGS_CONTEXT);

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        const response = await apiInstance.getUserDetailTrainings({
          userId: requestUserId,
          ...queryString,
          ...filtersQuery,
          sortingColumn: paginateOptions.sortingColumn,
        });
        if (this.userId !== requestUserId) return;
        if (response) {
          this.userTrainings = response;
        }
      } catch (error) {
        feedback.error(`erreur interne`, "small");
      }
    },

    /**
     * Parcours tab — GET /userDetail/user-learning-paths/:userId.
     * Standard grid, no filters. sortField: addedAt|name|completionRate|durationHours|score (default
     * addedAt). Returns an empty page rather than an error when the company's learning paths module
     * is off — the tab is gated on the same flag in UserDetailModal, so that path shouldn't be hit.
     */
    async fetchLearningPaths() {
      if (!this.userId) return;
      const requestUserId = this.userId;
      const requestSeq = ++learningPathsRequestSeq;
      const pagination = usePagination(PaginationKeysEnum.USER_DETAIL_LEARNING_PATHS_PAGINATION);

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());

      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        const response = await apiInstance.getUserDetailLearningPaths({
          userId: requestUserId,
          ...queryString,
          sortingColumn: paginateOptions.sortingColumn,
        });
        // Drop the answer if the user changed OR a later request was fired meanwhile.
        if (this.userId !== requestUserId || requestSeq !== learningPathsRequestSeq) return;
        if (response) {
          this.userLearningPaths = response;
        }
      } catch (error) {
        // Same guard as the success path: a superseded request can reject *after* a newer one has
        // already rendered its rows, and surfacing that would flip the tab to the error state on
        // top of valid results.
        if (this.userId !== requestUserId || requestSeq !== learningPathsRequestSeq) return;
        feedback.error(`erreur interne`, "small");
        // Rethrow so fetchWithStateHandling flips isError and the tab shows "Une erreur est
        // survenue." — swallowing here would render the empty state instead, i.e. a failed request
        // is indistinguishable from a user with no parcours.
        throw error;
      }
    },

    /** Habilitations tab — GET /userDetail/user-habilitations/:userId (standard grid, no filters). */
    async fetchHabilitations() {
      if (!this.userId) return;
      const requestUserId = this.userId;
      const pagination = usePagination(PaginationKeysEnum.USER_DETAIL_HABILITATIONS_PAGINATION);

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());

      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        const response = await apiInstance.getUserDetailHabilitations({
          userId: requestUserId,
          ...queryString,
          sortingColumn: paginateOptions.sortingColumn,
        });
        if (this.userId !== requestUserId) return;
        if (response) {
          this.userHabilitations = response;
        }
      } catch (error) {
        feedback.error(`erreur interne`, "small");
      }
    },

    /**
     * Tab counters — GET /userDetail/user-counts/:userId → { requests, trainings, habilitations }.
     * Fired on open() so every tab badge can render at once. Supplementary data → fails silently
     * (a missing count just hides that badge, it never blocks the modal).
     */
    async fetchCounts() {
      if (!this.userId) return;
      const requestUserId = this.userId;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        const data = await apiInstance.getUserDetailCounts({ userId: requestUserId });
        if (this.userId !== requestUserId) return;
        this.userCounts = data ?? null;
      } catch (error) {
        if (this.userId === requestUserId) this.userCounts = null;
      }
    },
  },
});
